
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { CharacterData, ActionName, POINT_TABLE, ACTION_NAMES, MIN_TIER, MAX_TIER, INITIAL_TIER, getInitialCharactersData, CustomAction } from './config';
import TierControlCard from './components/TierControlCard';
import CharacterCard from './components/CharacterCard';
import ConfirmationModal from './components/ConfirmationModal';
import SortOrderModal from './components/SortOrderModal';
import UpdateNotification from './components/UpdateNotification';

// --- LocalStorage Logic ---
const SAVED_STATE_KEY = 'chaos-simulator-state';

const loadState = () => {
  try {
    const serializedState = localStorage.getItem(SAVED_STATE_KEY);
    if (serializedState === null) {
      return undefined;
    }
    return JSON.parse(serializedState);
  } catch (err) {
    console.error("讀取儲存資料時發生錯誤:", err);
    return undefined;
  }
};

const savedState = loadState();

// --- Helper Functions ---

const getPointsForAction = (actionName: string, count: number, customActions: CustomAction[]): number => {
    const custom = customActions.find(c => c.id === actionName);
    if (custom) {
        return (count || 0) * custom.points;
    }

    const points = POINT_TABLE[actionName as keyof typeof POINT_TABLE];
    if (!points) return 0;

    const validCount = count || 0;
    if (validCount <= 0) return 0;

    if (validCount <= 5) {
        return points[validCount - 1];
    } else {
        const costOfStep5 = points[4] - points[3];
        return points[4] + (validCount - 5) * costOfStep5;
    }
};

const calculateTotalScore = (character: CharacterData, customActions: CustomAction[]): number => {
    return Object.keys(character.counts).reduce((total, name) => {
        return total + getPointsForAction(name, character.counts[name], customActions);
    }, 0);
};

const calculateMaxLimit = (tier: number): number => {
    const base = 30;
    const perTierBonus = 10;
    const validTier = Math.max(MIN_TIER, tier || MIN_TIER);
    return base + (validTier - 1) * perTierBonus;
};


const App: React.FC = () => {
    const [sharedTier, setSharedTier] = useState<number>(savedState?.sharedTier ?? INITIAL_TIER);
    const [customActions, setCustomActions] = useState<CustomAction[]>(savedState?.customActions ?? []);

    
    // Initialize Characters Data: Merge saved state with new structure if keys changed
    const [charactersData, setCharactersData] = useState<CharacterData[]>(() => {
        const defaults = getInitialCharactersData();
        if (savedState?.charactersData) {
            return savedState.charactersData.map((sChar: any, idx: number) => {
                const dChar = defaults[idx] || defaults[0]; // Fallback to default structure
                
                // Initialize counts with 0 for all current ACTION_NAMES
                const mergedCounts: any = { ...dChar.counts };

                // Only copy values from saved state if the key still exists in ACTION_NAMES
                // This handles migration from old rules to new rules safely
                ACTION_NAMES.forEach(key => {
                    if (sChar.counts && typeof sChar.counts[key] === 'number') {
                        mergedCounts[key] = sChar.counts[key];
                    }
                });

                // 向後相容：將舊的 "禁忌卡"、"賽季卡" 和 "中立卡" 次數轉移到 "中立卡/賽季卡"
                if (sChar.counts) {
                    let totalNeutralSeason = 0;
                    if (typeof sChar.counts['中立卡'] === 'number') totalNeutralSeason += sChar.counts['中立卡'];
                    if (typeof sChar.counts['賽季卡'] === 'number') totalNeutralSeason += sChar.counts['賽季卡'];
                    if (typeof sChar.counts['禁忌卡'] === 'number') totalNeutralSeason += sChar.counts['禁忌卡'];
                    
                    if (totalNeutralSeason > 0 && mergedCounts['中立卡/賽季卡'] === 0) {
                        mergedCounts['中立卡/賽季卡'] = totalNeutralSeason;
                    }
                }

                return { 
                    ...dChar, 
                    id: sChar.id || dChar.id,
                    name: sChar.name || dChar.name, 
                    counts: mergedCounts 
                };
            });
        }
        return defaults;
    });

    const [activeCharacterId, setActiveCharacterId] = useState<string>(savedState?.activeCharacterId ?? getInitialCharactersData()[0].id);
    const [isConfirmationEnabled, setIsConfirmationEnabled] = useState<boolean>(savedState?.isConfirmationEnabled ?? true);
    
    // Initialize Dark Mode: use saved state if available, otherwise check system preference
    const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
        if (savedState?.isDarkMode !== undefined) {
            return savedState.isDarkMode;
        }
        // Default to system preference if no saved state
        if (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return true;
        }
        return false;
    });
    
    // New state for Action Order - ensure we filter out obsolete keys from saved order
    const [actionOrder, setActionOrder] = useState<ActionName[]>(() => {
        if (savedState?.actionOrder) {
            // 自動將舊的 '禁忌卡', '賽季卡', '中立卡' 替換為 '中立卡/賽季卡'
            const remappedOrder = savedState.actionOrder.map((name: string) => {
                if (name === '禁忌卡' || name === '中立卡' || name === '賽季卡') return '中立卡/賽季卡';
                return name;
            });
            // 移除重複的項目
            const uniqueRemappedOrder = Array.from(new Set(remappedOrder));
            const validSavedOrder = uniqueRemappedOrder.filter((name: string) => ACTION_NAMES.includes(name as ActionName) || 
                (savedState.customActions && savedState.customActions.some((c: CustomAction) => c.id === name)));
            
            // 檢查是否為舊的預設順序，如果是，切換到新的預設順序
            const isOldDefaultOrder = validSavedOrder.join(',') === "移除起始卡,移除其他卡,中立卡/賽季卡,神閃,複製卡片,怪物卡(一般),怪物卡(稀有),怪物卡(傳說)";
            if (isOldDefaultOrder) {
                return ACTION_NAMES;
            }

            // Append any new keys that weren't in the saved order
            const missingKeys = ACTION_NAMES.filter(name => !validSavedOrder.includes(name));
            return [...validSavedOrder, ...missingKeys];
        }
        return ACTION_NAMES;
    });

    const [confirmationState, setConfirmationState] = useState<{
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: (() => void) | null;
    }>({ isOpen: false, title: '', message: '', onConfirm: null });

    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    
    // Service Worker State
    const [waitingWorker, setWaitingWorker] = useState<ServiceWorker | null>(null);
    const [showUpdateBtn, setShowUpdateBtn] = useState(false);

    // Effect for Service Worker Registration
    useEffect(() => {
        if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
            navigator.serviceWorker.register('./service-worker.js')
                .then(registration => {
                    console.log('Service Worker registered with scope:', registration.scope);

                    // If there's a waiting worker, we can update
                    if (registration.waiting) {
                        setWaitingWorker(registration.waiting);
                        setShowUpdateBtn(true);
                    }

                    // Detect new updates
                    registration.addEventListener('updatefound', () => {
                        const newWorker = registration.installing;
                        if (newWorker) {
                            newWorker.addEventListener('statechange', () => {
                                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                                    setWaitingWorker(newWorker);
                                    setShowUpdateBtn(true);
                                }
                            });
                        }
                    });
                })
                .catch(err => {
                    // Silently ignore specific environment errors (like iframe origin mismatches)
                    // to prevent confusing users in preview environments.
                    if (err.message && err.message.includes('origin')) {
                        console.warn('Service Worker registration skipped due to environment restrictions (Origin Mismatch). This is normal in some preview environments.');
                    } else {
                        console.error('Service Worker registration failed:', err);
                    }
                });
        }
    }, []);

    const handleUpdateApp = useCallback(() => {
        if (waitingWorker) {
            waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        }
        setShowUpdateBtn(false);
        window.location.reload();
    }, [waitingWorker]);
    
    // Effect for saving state to localStorage
    useEffect(() => {
        try {
            const stateToSave = {
                sharedTier,
                charactersData,
                activeCharacterId,
                isConfirmationEnabled,
                isDarkMode,
                actionOrder, // Save the order
                customActions, // Save custom actions
            };
            const serializedState = JSON.stringify(stateToSave);
            localStorage.setItem(SAVED_STATE_KEY, serializedState);
        } catch (err) {
            console.error("儲存資料時發生錯誤:", err);
        }
    }, [sharedTier, charactersData, activeCharacterId, isConfirmationEnabled, isDarkMode, actionOrder, customActions]);
    
    // Effect for toggling dark mode class
    useEffect(() => {
        document.documentElement.classList.toggle('dark', isDarkMode);
    }, [isDarkMode]);

    const handleTierChange = useCallback((newTier: number) => {
        const validatedTier = Math.max(MIN_TIER, Math.min(MAX_TIER, newTier));
        setSharedTier(validatedTier);
    }, []);
    
    const performResetAll = useCallback(() => {
        // Only reset character counts, preserve names and sharedTier
        setCharactersData(prevData =>
            prevData.map(char => {
                const initialCharData = getInitialCharactersData().find(c => c.id === char.id);
                return { 
                    ...char, 
                    counts: initialCharData ? { ...initialCharData.counts } : char.counts 
                };
            })
        );
        // No need to clear localStorage, useEffect will overwrite it with initial state
    }, []);

    const handleResetAll = useCallback(() => {
        if (isConfirmationEnabled) {
            setConfirmationState({
                isOpen: true,
                title: '確認全部重置',
                message: '您確定要重置所有角色的分數嗎？(TIER 等級將保留) 此操作無法復原。',
                onConfirm: performResetAll,
            });
        } else {
            performResetAll();
        }
    }, [isConfirmationEnabled, performResetAll]);

    const performResetCharacter = useCallback((charId: string) => {
        setCharactersData(prevData =>
            prevData.map(char => {
                if (char.id !== charId) return char;
                const initialCharData = getInitialCharactersData().find(c => c.id === charId);
                return { ...char, counts: initialCharData ? { ...initialCharData.counts } : char.counts };
            })
        );
    }, []);

    const handleResetCharacter = useCallback((charId: string, charName: string) => {
        if (isConfirmationEnabled) {
             setConfirmationState({
                isOpen: true,
                title: `確認重置角色`,
                message: `您確定要重置角色「${charName}」的所有分數嗎？此操作無法復原。`,
                onConfirm: () => performResetCharacter(charId),
            });
        } else {
            performResetCharacter(charId);
        }
    }, [isConfirmationEnabled, performResetCharacter]);

    const handleCountChange = useCallback((charId: string, actionName: ActionName, newCount: number) => {
        setCharactersData(prevData =>
            prevData.map(char => {
                if (char.id !== charId) return char;

                const updatedCounts = { ...char.counts };
                let validatedCount = Math.max(0, newCount);
                
                // Update the count for the specific action
                updatedCounts[actionName] = validatedCount;

                return { ...char, counts: updatedCounts };
            })
        );
    }, []);

    const handleNameChange = useCallback((charId: string, newName: string) => {
        setCharactersData(prevData =>
            prevData.map(char => (char.id === charId ? { ...char, name: newName } : char))
        );
    }, []);

    const closeConfirmationModal = () => {
        setConfirmationState({ isOpen: false, title: '', message: '', onConfirm: null });
    };

    const handleConfirm = () => {
        if (confirmationState.onConfirm) {
            confirmationState.onConfirm();
        }
        closeConfirmationModal();
    };

    const handleSaveOrder = (newOrder: ActionName[], newCustomActions: CustomAction[]) => {
        setCustomActions(newCustomActions);
        setActionOrder(newOrder);
        setIsSortModalOpen(false);
    };

    const maxLimit = useMemo(() => calculateMaxLimit(sharedTier), [sharedTier]);
    
    const characterScores = useMemo(() => {
        const scores: { [key: string]: number } = {};
        charactersData.forEach(char => {
            scores[char.id] = calculateTotalScore(char, customActions);
        });
        return scores;
    }, [charactersData, customActions]);

    return (
        <div className="container mx-auto max-w-7xl p-4 lg:p-8 min-h-screen flex flex-col">
            
            <h1 className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mb-6 text-center flex items-baseline justify-center gap-2">
                卡厄思計分模擬器
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">v1.9.9</span>
            </h1>
            
            <TierControlCard 
                tier={sharedTier}
                maxLimit={maxLimit}
                onTierChange={handleTierChange}
                onResetAll={handleResetAll}
                isConfirmationEnabled={isConfirmationEnabled}
                onConfirmationToggle={() => setIsConfirmationEnabled(prev => !prev)}
                isDarkMode={isDarkMode}
                onThemeToggle={() => setIsDarkMode(prev => !prev)}
                onOpenSortModal={() => setIsSortModalOpen(true)}
            />

            <div id="characters-container" className="flex-grow">
                {/* --- Tab view for smaller screens (< 1024px) --- */}
                <div className="lg:hidden">
                    <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
                        {charactersData.map(char => (
                            <button
                                key={char.id}
                                onClick={() => setActiveCharacterId(char.id)}
                                className={`px-4 py-2 font-semibold text-base transition-colors duration-200 w-1/3 text-center ${
                                    activeCharacterId === char.id
                                        ? 'border-b-2 border-indigo-500 text-indigo-600 bg-indigo-50 dark:bg-slate-800 dark:text-indigo-400 rounded-t-lg'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-indigo-500 hover:bg-gray-100 dark:hover:bg-slate-800'
                                }`}
                            >
                                {char.name}
                            </button>
                        ))}
                    </div>
                    <div>
                        {charactersData
                            .filter(char => char.id === activeCharacterId)
                            .map(char => (
                                 <CharacterCard
                                    key={char.id}
                                    character={char}
                                    totalScore={characterScores[char.id]}
                                    maxLimit={maxLimit}
                                    onCountChange={handleCountChange}
                                    onNameChange={handleNameChange}
                                    onResetCharacter={handleResetCharacter}
                                    actionOrder={actionOrder}
                                    customActions={customActions}
                                />
                            ))}
                    </div>
                </div>

                {/* --- Grid view for larger screens (>= 1024px) --- */}
                <div className="hidden lg:grid lg:grid-cols-3 lg:gap-6">
                    {charactersData.map(char => (
                        <CharacterCard
                            key={char.id}
                            character={char}
                            totalScore={characterScores[char.id]}
                            maxLimit={maxLimit}
                            onCountChange={handleCountChange}
                            onNameChange={handleNameChange}
                            onResetCharacter={handleResetCharacter}
                            actionOrder={actionOrder}
                            customActions={customActions}
                        />
                    ))}
                </div>
            </div>

            <ConfirmationModal
                isOpen={confirmationState.isOpen}
                title={confirmationState.title}
                message={confirmationState.message}
                onClose={closeConfirmationModal}
                onConfirm={handleConfirm}
            />

            <SortOrderModal
                isOpen={isSortModalOpen}
                currentOrder={actionOrder}
                customActions={customActions}
                onClose={() => setIsSortModalOpen(false)}
                onSave={handleSaveOrder}
            />

            {showUpdateBtn && <UpdateNotification onUpdate={handleUpdateApp} />}
        </div>
    );
};

export default App;
